import { ApplicationState } from '../../rootReducer';
import { AnalyticsDataState, HttpDataState, MsAnalyticsDataState, TabDataState } from './analyticsDataTypes';
import {
  copyDataState,
  fetchAnalyticsInspectionDataDetail,
  fetchHttpInspectionData,
  isTabActive,
  updateCurrentDataContext,
  updateTabData,
} from './analyticsDataActions';
import { BlazeMeterApiResponse } from '../../../types/rest/blazeMeterApiResponse';
import { showToastNotification } from '../../ui/toasts/toastsActions';
import { ToastType } from '../../ui/toasts/toastsTypes';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import _ from 'lodash';
import { HttpRequest } from '../../../types/rest/analyticsTypes';
import {
  HTTP_CALL_SET_IS_REQUEST_SENT,
  HTTP_CALL_SET_VIEW_IS_LOADING_CALLS,
  HTTP_CALL_VIEW_UPDATE,
} from './analyticsDataActionTypes';
import { action } from 'typesafe-actions';
import {
  selectHttpCallsViewTriggered,
  setLoadingHttpCallDetail,
} from '../ui/subViews/httpCallsView/httpCallsViewActions';
import { filterOnlyExecutedHttpInspectionData } from './analyticsWebhookActions';

export const fetchHttpCallsThunk = (
  mockServiceId: number | string | null,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    if (_.isNil(mockServiceId)) {
      console.error('No virtual service id available.');
      return;
    }

    // Set loading to true in current http call view
    dispatch(setHttpCallsRequestSent(true));
    dispatch(setLoadingHttpCalls(true));
    const dataState: AnalyticsDataState = copyDataState(getState);

    try {
      const response: BlazeMeterApiResponse = (await fetchHttpInspectionData(mockServiceId)).data;
      const httpCalls = filterOnlyExecutedHttpInspectionData(response.result);

      const activeDataContext = dataState.activeDataContext as MsAnalyticsDataState;
      activeDataContext.httpCall.httpRequests = httpCalls;
      activeDataContext.httpCall.isLoadingData = false;
      activeDataContext.httpCall.lastRefreshTs = new Date().getTime();

      // Save result to data array
      dispatch(updateTabData(activeDataContext));

      // Update current data context (if still active)
      if (isTabActive(mockServiceId)) {
        dispatch(updateCurrentHttpViewData(activeDataContext.httpCall));
      }

      //Preselect first item and do the reload of details
      if (httpCalls.length !== 0) {
        const firstRowId = parseInt(httpCalls[0].id);
        dispatch(fetchHttpCallDetailThunk(firstRowId, mockServiceId));
      }
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve analytics data', errorBody));
      if (isTabActive(mockServiceId)) {
        dispatch(setLoadingHttpCalls(false));
      }
    }
  };
};
/**
 * Merges request + detail data.
 * @param httpRequest
 * @param httpCallDetail
 */
export const buildHttpDetail = (httpRequest: HttpRequest, httpCallDetail) => {
  return {
    request: {
      queryParams: httpRequest.arguments,
      header: httpCallDetail.requestHeaders,
      bodyContent: !_.isNil(httpCallDetail.requestBody)
        ? Buffer.from(httpCallDetail.requestBody, 'base64').toString()
        : null,
      resultCode: httpCallDetail.statusCode,
    },
    response: {
      queryParams: [],
      header: httpCallDetail.responseHeaders ? httpCallDetail.responseHeaders : [],
      bodyContent: !_.isNil(httpCallDetail.responseBody)
        ? Buffer.from(httpCallDetail.responseBody, 'base64').toString()
        : null,
      resultCode: httpCallDetail.statusCode,
    },
  };
};

/**
 * Select http call in the request table. Marks the item in the table as selected and reloads
 * detail of the request.
 * @param requestId - id of the request to be loaded
 * @param tabId - id of the tab which requests belongs to
 */
export const fetchHttpCallDetailThunk = (
  httpCallId: number,
  tabId: number | string | null,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    //Do not continue if tab / request id is empty
    if (_.isNil(tabId) || _.isNil(httpCallId)) {
      return;
    }

    //Add selected request to the ui state
    const tabData: TabDataState[] = getState().analytics.data.tabData;
    const tab = tabData.find((item: TabDataState) => String(item.tabId) === String(tabId));
    const httpCall: HttpRequest = tab.httpCall.httpRequests.find((webhook: any) => webhook.id === httpCallId);

    if (isTabActive(tabId)) {
      await dispatch(selectHttpCallsViewTriggered(httpCall));
      await dispatch(setLoadingHttpCallDetail(true));
    }

    try {
      const response: BlazeMeterApiResponse = (await fetchAnalyticsInspectionDataDetail(httpCall.id)).data;
      const updatedTab = _.cloneDeep(tab);
      updatedTab.httpCall.detail = buildHttpDetail(httpCall, response.result);
      updatedTab.httpCall.isLoadingDetails = false;
      updatedTab.httpCall.isOutdated = false;
      updatedTab.httpCall.isDetailRequested = true;

      // Update data
      dispatch(updateTabData(updatedTab));

      //Is tab still active, update current inspection view
      if (isTabActive(tabId)) {
        dispatch(updateCurrentHttpViewData(updatedTab.httpCall));
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setHttpCallDetailOutdated(getState, tabId, dispatch);
      } else {
        const errorBody = _.get(error, 'response.data.error', error.toString());
        dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve http call detail data', errorBody));
      }
    }
  };
};

const setHttpCallDetailOutdated = (
  getState: any,
  tabId: number | string,
  dispatch: ThunkDispatch<ApplicationState, null, AnyAction>,
) => {
  const currentDataState: MsAnalyticsDataState = copyDataState(getState).activeDataContext;
  // Check if we are in same data context
  if (isTabActive(tabId)) {
    currentDataState.httpCall.isLoadingDetails = false;
    currentDataState.httpCall.isOutdated = true;
    dispatch(updateCurrentDataContext(currentDataState));
  }
};

export const updateCurrentHttpViewData = (httpCallViewData: HttpDataState) =>
  action(HTTP_CALL_VIEW_UPDATE, httpCallViewData);
export const setLoadingHttpCalls = (isLoading: boolean) => action(HTTP_CALL_SET_VIEW_IS_LOADING_CALLS, isLoading);
export const setHttpCallsRequestSent = (isHttpCallsRequested: boolean) =>
  action(HTTP_CALL_SET_IS_REQUEST_SENT, isHttpCallsRequested);
